import { useState, useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { reviewSchema } from '@/utils/validation'
import { createReview, getReviewsForSeller } from '@/services/api/reviews'
import { useAuth } from '@/hooks/useAuth'
import { useUIStore } from '@/store/uiStore'
import { Button } from '@/components/common/Button'
import { Star } from 'lucide-react'
import type { Review } from '@/types/review'

type ReviewFormData = {
  rating: number
  comment: string
}

interface ReviewSectionProps {
  sellerId: string
  sellerName: string
  listingId?: string
}

function StarRating({ rating, onRate, interactive = false }: { rating: number; onRate?: (r: number) => void; interactive?: boolean }) {
  const [hover, setHover] = useState(0)
  return (
    <div className="flex gap-0.5">
      {[1, 2, 3, 4, 5].map((star) => (
        <button
          key={star}
          type="button"
          disabled={!interactive}
          onClick={() => onRate?.(star)}
          onMouseEnter={() => interactive && setHover(star)}
          onMouseLeave={() => interactive && setHover(0)}
          className={interactive ? 'cursor-pointer' : 'cursor-default'}
        >
          <Star
            className={`w-5 h-5 ${
              star <= (hover || rating)
                ? 'text-primary-500 fill-primary-500'
                : 'text-slate-300'
            }`}
          />
        </button>
      ))}
    </div>
  )
}

export function ReviewSection({ sellerId, sellerName, listingId }: ReviewSectionProps) {
  const { user } = useAuth()
  const { showToast, openModal } = useUIStore()
  const [reviews, setReviews] = useState<Review[]>([])
  const [loading, setLoading] = useState(true)
  const [submitting, setSubmitting] = useState(false)
  const [showForm, setShowForm] = useState(false)
  const [selectedRating, setSelectedRating] = useState(0)

  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    reset,
  } = useForm<ReviewFormData>({
    resolver: zodResolver(reviewSchema),
  })

  useEffect(() => {
    const load = async () => {
      try {
        const data = await getReviewsForSeller(sellerId)
        setReviews(data)
      } catch (error) {
        console.error('Error loading reviews:', error)
      } finally {
        setLoading(false)
      }
    }
    load()
  }, [sellerId])

  const handleRatingSelect = (rating: number) => {
    setSelectedRating(rating)
    setValue('rating', rating)
  }

  const onSubmit = async (data: ReviewFormData) => {
    if (!user) {
      openModal('login')
      return
    }
    setSubmitting(true)
    try {
      const id = await createReview(
        user.uid,
        user.displayName || 'Anonymous',
        sellerId,
        data.rating,
        data.comment,
        listingId
      )
      setReviews((prev) => [
        {
          id,
          reviewerId: user.uid,
          reviewerName: user.displayName || 'Anonymous',
          sellerId,
          listingId,
          rating: data.rating,
          comment: data.comment,
          createdAt: { toDate: () => new Date() } as any,
        },
        ...prev,
      ])
      showToast('Review submitted!')
      setShowForm(false)
      reset()
      setSelectedRating(0)
    } catch {
      showToast('Failed to submit review', 'error')
    } finally {
      setSubmitting(false)
    }
  }

  const avgRating = reviews.length > 0
    ? reviews.reduce((sum, r) => sum + r.rating, 0) / reviews.length
    : 0

  const canReview = user && user.uid !== sellerId

  return (
    <div className="mb-6">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-semibold">
          Reviews {reviews.length > 0 && `(${reviews.length})`}
        </h2>
        {canReview && !showForm && (
          <Button size="sm" variant="outline" onClick={() => setShowForm(true)}>
            Write a Review
          </Button>
        )}
      </div>

      {/* Average rating */}
      {reviews.length > 0 && (
        <div className="flex items-center gap-3 mb-4">
          <StarRating rating={Math.round(avgRating)} />
          <span className="text-lg font-bold">{avgRating.toFixed(1)}</span>
          <span className="text-sm text-slate-500">({reviews.length} reviews)</span>
        </div>
      )}

      {/* Write review form */}
      {showForm && (
        <div className="card p-4 mb-4">
          <h3 className="font-medium mb-3">Review {sellerName}</h3>
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-3">
            <div>
              <label className="label">Rating</label>
              <StarRating rating={selectedRating} onRate={handleRatingSelect} interactive />
              <input type="hidden" {...register('rating', { valueAsNumber: true })} />
              {errors.rating && <p className="error-text">{errors.rating.message}</p>}
            </div>
            <div>
              <label className="label">Your Review</label>
              <textarea
                placeholder="Share your experience..."
                className="input min-h-[80px]"
                {...register('comment')}
              />
              {errors.comment && <p className="error-text">{errors.comment.message}</p>}
            </div>
            <div className="flex gap-2">
              <Button type="submit" size="sm" loading={submitting}>Submit Review</Button>
              <Button type="button" size="sm" variant="outline" onClick={() => { setShowForm(false); reset(); setSelectedRating(0) }}>
                Cancel
              </Button>
            </div>
          </form>
        </div>
      )}

      {/* Reviews list */}
      {loading ? (
        <p className="text-sm text-slate-500">Loading reviews...</p>
      ) : reviews.length === 0 ? (
        <p className="text-sm text-slate-500">No reviews yet.</p>
      ) : (
        <div className="space-y-3">
          {reviews.map((review) => {
            const date = review.createdAt?.toDate?.()
            return (
              <div key={review.id} className="card p-3 sm:p-4">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-1 sm:gap-2 mb-2">
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 rounded-full bg-slate-200 flex items-center justify-center text-sm font-bold">
                      {review.reviewerName[0]?.toUpperCase()}
                    </div>
                    <div>
                      <p className="font-medium text-sm">{review.reviewerName}</p>
                      {date && (
                        <p className="text-xs text-slate-400">{date.toLocaleDateString('en-NZ')}</p>
                      )}
                    </div>
                  </div>
                  <StarRating rating={review.rating} />
                </div>
                <p className="text-sm text-slate-600">{review.comment}</p>
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}
